import { useState } from 'react'
import { Check, Diamond, Target, TriangleAlert, X } from 'lucide-react'
import type { StructuredReportStep } from '../types'

const DONE_SENTINEL = '_done'
const ICON_SIZE = 16

export function StepIcon({ step }: { step: StructuredReportStep }) {
  if (step.result === 'fail') {
    return <X size={ICON_SIZE} className="text-red-500" />
  }
  if (step.recovery) {
    return <TriangleAlert size={ICON_SIZE} className="text-yellow-500" />
  }
  if (step.type === 'decision') {
    return (
      <Diamond size={ICON_SIZE} className="text-blue-500" fill="currentColor" />
    )
  }
  if (step.type === 'extract') {
    return <Target size={ICON_SIZE} className="text-purple-500" />
  }
  return <Check size={ICON_SIZE} className="text-green-500" />
}

function formatExtractValue(value: unknown): string {
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

export function DecisionDetail({ step }: { step: StructuredReportStep }) {
  const decision = step.decision!
  const isDone = decision.cursor_at === DONE_SENTINEL
  return (
    <div className="mt-1 mb-1 max-w-2xl rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        {decision.requests != null && <span>{decision.requests} requests</span>}
        {decision.tokens != null && (
          <span>· {decision.tokens.toLocaleString()} tokens</span>
        )}
      </div>
      <p className="text-xs text-gray-700 mb-1">
        →{' '}
        <span className="font-mono font-semibold">
          {isDone ? 'done' : decision.cursor_at}
        </span>
      </p>
      {decision.reasoning && (
        <p className="text-sm text-gray-800">{decision.reasoning}</p>
      )}
    </div>
  )
}

export function ExtractDetail({ step }: { step: StructuredReportStep }) {
  const extract = step.extract!
  return (
    <div className="mt-1 mb-1 max-w-2xl rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        {extract.requests != null && <span>{extract.requests} requests</span>}
        {extract.tokens != null && (
          <span>· {extract.tokens.toLocaleString()} tokens</span>
        )}
      </div>
      <p className="text-xs text-gray-700 mb-1">
        <span className="text-gray-500">Captured </span>
        <span className="font-mono">{extract.name}</span>
        <span className="text-gray-400"> = </span>
        <span className="font-mono font-semibold">
          {formatExtractValue(extract.value)}
        </span>
      </p>
      {extract.observed && (
        <p className="text-sm text-gray-800 italic">{extract.observed}</p>
      )}
    </div>
  )
}

export function RecoveryDetail({ step }: { step: StructuredReportStep }) {
  const [showAction, setShowAction] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  const recovery = step.recovery!
  const failed = step.result === 'fail'

  return (
    <div
      className={`mt-1 mb-1 max-w-2xl rounded-md border px-3 py-2 text-sm ${
        failed ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'
      }`}>
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        {recovery.requests != null && <span>{recovery.requests} requests</span>}
        {recovery.tokens != null && (
          <span>· {recovery.tokens.toLocaleString()} tokens</span>
        )}
      </div>
      {recovery.postcondition_already_met && (
        <p className="text-xs text-yellow-800 italic mb-1">
          Postcondition was already met (validation was wrong)
        </p>
      )}
      {recovery.analysis && (
        <p className="text-sm text-gray-800">{recovery.analysis}</p>
      )}
      {recovery.reason && !recovery.analysis && (
        <p className="text-sm text-gray-800">{recovery.reason}</p>
      )}
      {recovery.action && (
        <div className="mt-2">
          <button
            onClick={() => setShowAction((value) => !value)}
            className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer">
            {showAction ? '▾' : '▸'} Action
          </button>
          {showAction && (
            <pre className="mt-1 rounded bg-gray-900 text-gray-100 text-xs p-2 overflow-x-auto">
              <code>{recovery.action}</code>
            </pre>
          )}
        </div>
      )}
      {recovery.validation && (
        <div className="mt-2">
          <button
            onClick={() => setShowValidation((value) => !value)}
            className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer">
            {showValidation ? '▾' : '▸'} Validation
          </button>
          {showValidation && (
            <pre className="mt-1 rounded bg-gray-900 text-gray-100 text-xs p-2 overflow-x-auto">
              <code>{recovery.validation}</code>
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
